/**
 * HTML Sanitization Utilities
 *
 * SECURITY: These utilities prevent XSS attacks by sanitizing HTML content
 * before rendering. Use DOMPurify for user-generated HTML content.
 */

import DOMPurify, { type Config } from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks
 *
 * SECURITY: Use this for any HTML content that comes from:
 * - User input
 * - API responses
 * - Database content
 * - External sources
 *
 * @param dirty - Potentially unsafe HTML string
 * @param options - DOMPurify configuration options
 * @returns Sanitized HTML string
 */
export function sanitizeHtml(
  dirty: string,
  options?: Config
): string {
  // Default configuration: allow common formatting tags but strip scripts
  const defaultConfig: Config = {
    ALLOWED_TAGS: [
      // Text formatting
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'ins',
      'mark', 'small', 'sub', 'sup', 'code', 'pre', 'kbd', 'samp', 'var',
      // Headings
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      // Lists
      'ul', 'ol', 'li', 'dl', 'dt', 'dd',
      // Structure
      'div', 'span', 'blockquote', 'hr',
      // Tables
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
      // Links (href will be validated by ALLOWED_URI_REGEXP)
      'a',
    ],
    ALLOWED_ATTR: [
      // Global attributes
      'class', 'id', 'title', 'lang', 'dir',
      // Links
      'href', 'target', 'rel',
      // Tables
      'colspan', 'rowspan', 'scope',
    ],
    // Only allow http, https, mailto links
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    // Prevent DOM clobbering
    SANITIZE_DOM: true,
    // Strip dangerous protocols
    ALLOW_DATA_ATTR: false,
  };

  const config = options ? { ...defaultConfig, ...options } : defaultConfig;

  return DOMPurify.sanitize(dirty, config) as string;
}

/**
 * Sanitize HTML content for markdown/prose output
 *
 * More permissive configuration for rendered markdown content
 */
export function sanitizeMarkdownHtml(dirty: string): string {
  return sanitizeHtml(dirty, {
    ALLOWED_TAGS: [
      // All default tags plus images for markdown
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'ins',
      'mark', 'small', 'sub', 'sup', 'code', 'pre', 'kbd', 'samp', 'var',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'dl', 'dt', 'dd',
      'div', 'span', 'blockquote', 'hr',
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
      'a', 'img',
    ],
    ALLOWED_ATTR: [
      'class', 'id', 'title', 'lang', 'dir',
      'href', 'target', 'rel',
      'colspan', 'rowspan', 'scope',
      // Image attributes
      'src', 'alt', 'width', 'height', 'loading',
    ],
  });
}

/**
 * Strip all HTML tags, returning plain text
 *
 * SECURITY: Use when you need text content only, no HTML rendering
 */
export function stripHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  }) as string;
}
